import React, { useState, useEffect, useContext } from "react";
import "./Agreements.css";
import AddAgreement from "./AddAgreement/AddAgreement";
import AgreementDetail from "./AgreementDetail/AgreementDetail";
import SendAgreementApprovalModal from "./SendAgreementApprovalModal/SendAgreementApprovalModal";
import {
  AGREEMENT_LIST_STATUS_LABELS,
  canEditAgreement,
  canSendAgreementForApproval,
  canShowDeleteApprovalAction,
  getEditAgreementTitle,
  enrichAgreementsWithApprovalInitiators,
  getAgreementListStatusBadgeClass,
  getDeleteApprovalTitle,
  getSendForApprovalTitle,
  isAgreementApprovalInitiator,
  resolveAgreementApprovalId,
  resolveAgreementListStatus,
} from "./agreementsListHelpers";
import {
  deleteAgreement,
  deleteAgreementApproval,
  exportAgreementXlsx,
  getAgreements,
} from "../../../../api/agreementsApi";
import { getProjectEstimate } from "../../../../api/projectEstimatesApi";
import { getAllCounterparties, getUsersDirectory } from "../../../../api/api";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  formatAgreementNumber,
  formatUserDisplayName,
  getCounterpartyDisplayName as   getAgreementPerformerDisplayName,
} from "../AgreementsV2/agreementsHelpers";
import { getAgreementSectionModuleTitles } from "./agreementFormHelpers";

const ESTIMATE_MODULE_SOURCES = [
  { key: "bim_coordination", title: "BIM координирование" },
  { key: "quality_control", title: "Контроль качества" },
  { key: "project_delivery", title: "Ведение проекта" },
  { key: "project_management", title: "Управление проектом" },
];

const getModuleMoneyTotal = (money) =>
  Math.round(
    (Number(money?.direct) || 0) + (Number(money?.bonus) || 0),
  );

const collectEstimateModules = (estimate) =>
  ESTIMATE_MODULE_SOURCES.map((source) => {
    const block = estimate?.[source.key] || {};
    const roles = (Array.isArray(block.roles) ? block.roles : [])
      .filter((role) => role?.role)
      .map((role) => ({
        name: role.role,
        cost: getModuleMoneyTotal(role.money_expenditures),
        sectionId:
          role.section_id != null && Number.isFinite(Number(role.section_id))
            ? Number(role.section_id)
            : null,
      }));
    return {
      title: source.title,
      roles,
      total: getModuleMoneyTotal(block.total_money_expenditures),
    };
  }).filter((module) => module.roles.length > 0 || module.total > 0);

const inferAgreementModuleLabels = (price, modules) => {
  const target = Math.round(Number(price) || 0);
  if (!target || !Array.isArray(modules) || modules.length === 0) return [];

  const roleMatches = modules.flatMap((module) =>
    module.roles
      .filter((role) => role.cost === target)
      .map((role) => `${module.title} · ${role.name}`),
  );
  if (roleMatches.length > 0) return roleMatches;

  return modules
    .filter((module) => {
      const rolesTotal = module.roles.reduce((sum, role) => sum + role.cost, 0);
      return module.total === target || rolesTotal === target;
    })
    .map((module) => module.title);
};

const getCounterpartyItemDisplayName = (counterparty) =>
  counterparty?.display_name ||
  counterparty?.full_name ||
  counterparty?.full_company_name ||
  counterparty?.short_company_name ||
  counterparty?.short_name ||
  (counterparty?.id != null ? `Контрагент #${counterparty.id}` : "");

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Agreements = ({ projectId }) => {
  const { userData } = useContext(UserContext);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [apiPage, setApiPage] = useState(1);
  const pageSize = 20;

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [agreementModal, setAgreementModal] = useState({
    open: false,
    agreementId: null,
  });
  const [sectionNameById, setSectionNameById] = useState({});
  const [estimateModules, setEstimateModules] = useState([]);
  const [counterpartyNameById, setCounterpartyNameById] = useState({});
  const [performerNameById, setPerformerNameById] = useState({});
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteApprovalAgreement, setPendingDeleteApprovalAgreement] =
    useState(null);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [agreementToApprove, setAgreementToApprove] = useState(null);
  const [downloadingAgreementId, setDownloadingAgreementId] = useState(null);

  const loadAgreements = async ({ showLoading = true } = {}) => {
    if (!projectId) {
      setAgreements([]);
      setTotalCount(0);
      return;
    }
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await getAgreements({
        project_id: Number(projectId),
        page: apiPage,
        page_size: pageSize,
        ordering: "number",
      });
      const results = Array.isArray(data.results) ? data.results : [];
      const enriched = await enrichAgreementsWithApprovalInitiators(results);
      setAgreements(enriched);
      setTotalCount(data.count ?? 0);
    } catch (err) {
      setError(err.message || "Не удалось загрузить соглашения");
      setAgreements([]);
      setTotalCount(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) {
        setAgreements([]);
        setTotalCount(0);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getAgreements({
          project_id: Number(projectId),
          page: apiPage,
          page_size: pageSize,
          ordering: "number",
        });
        if (cancelled) return;
        const results = Array.isArray(data.results) ? data.results : [];
        if (!cancelled) {
          const enriched = await enrichAgreementsWithApprovalInitiators(results);
          if (!cancelled) {
            setAgreements(enriched);
            setTotalCount(data.count ?? 0);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Не удалось загрузить соглашения");
          setAgreements([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, apiPage]);

  useEffect(() => {
    if (!projectId) {
      setSectionNameById({});
      setEstimateModules([]);
      return;
    }
    let cancelled = false;
    getProjectEstimate(Number(projectId))
      .then((estimate) => {
        if (cancelled) return;
        const map = {};
        const addSection = (item) => {
          const sectionId = item?.section?.id;
          const sectionName = item?.section?.name;
          if (sectionId != null && sectionName) {
            map[Number(sectionId)] = sectionName;
          }
        };
        (estimate?.contract_sections?.sections || []).forEach(addSection);
        (estimate?.construction_design || []).forEach(addSection);
        (estimate?.construction_design_project || []).forEach(addSection);
        setSectionNameById(map);
        setEstimateModules(collectEstimateModules(estimate));
      })
      .catch(() => {
        if (!cancelled) {
          setSectionNameById({});
          setEstimateModules([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAllCounterparties({ counterparty_side: "counterparty" }),
      getAllCounterparties({ counterparty_side: "our_company" }),
    ])
      .then(([counterparties, organizations]) => {
        if (cancelled) return;
        const map = {};
        [
          ...(Array.isArray(counterparties) ? counterparties : []),
          ...(Array.isArray(organizations) ? organizations : []),
        ].forEach((item) => {
          if (item?.id != null) {
            map[Number(item.id)] = getCounterpartyItemDisplayName(item);
          }
        });
        setCounterpartyNameById(map);
      })
      .catch(() => {
        if (!cancelled) setCounterpartyNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getUsersDirectory()
      .then((users) => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(users) ? users : []).forEach((user) => {
          if (user?.id != null) {
            map[Number(user.id)] = formatUserDisplayName(user);
          }
        });
        setPerformerNameById(map);
      })
      .catch(() => {
        if (!cancelled) setPerformerNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAgreements = () => loadAgreements({ showLoading: false });

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getAgreementPerformerName = (agreement) =>
    getAgreementPerformerDisplayName(
      agreement,
      counterpartyNameById,
      performerNameById,
    );

  const getAgreementSectionsLabels = (agreement) => {
    const sectionIds = Array.isArray(agreement?.section_ids)
      ? agreement.section_ids
      : [];
    const pricedSectionIds = new Set(
      (Array.isArray(agreement?.section_prices) ? agreement.section_prices : [])
        .map((item) => Number(item?.section_id))
        .filter((id) => Number.isFinite(id)),
    );
    if (sectionIds.length > 0) {
      return sectionIds.map((sectionId) => {
        const numericId = Number(sectionId);
        const name = sectionNameById[numericId];
        const storedTitles = pricedSectionIds.has(numericId)
          ? getAgreementSectionModuleTitles(agreement, numericId)
          : [];
        if (storedTitles.length > 0) {
          return name
            ? `${name} · ${storedTitles.join(", ")}`
            : storedTitles.join(", ");
        }
        return name || `Раздел #${numericId}`;
      });
    }
    const blockDetails = Array.isArray(agreement?.agreement_blocks_detail)
      ? agreement.agreement_blocks_detail
      : [];
    if (blockDetails.length > 0) {
      return blockDetails.map((b) => b?.block_name).filter(Boolean);
    }
    const blockIds = Array.isArray(agreement?.block_ids)
      ? agreement.block_ids
      : [];
    if (blockIds.length > 0) {
      return blockIds.map((blockId) => `Блок #${blockId}`);
    }
    return inferAgreementModuleLabels(agreement?.price, estimateModules);
  };

  const filteredAgreements = agreements.filter(
    (agreement) =>
      String(agreement.number ?? "").includes(searchQuery) ||
      String(agreement.company_id ?? "").includes(searchQuery) ||
      String(agreement.counterparty_id ?? "").includes(searchQuery) ||
      getAgreementPerformerName(agreement)
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      String(agreement.id ?? "").includes(searchQuery),
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const displayAgreements = filteredAgreements;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const handleDownloadAgreementDocument = async (agreement, e) => {
    e.stopPropagation();
    if (agreement?.id == null || downloadingAgreementId != null) return;
    setDownloadingAgreementId(agreement.id);
    setError(null);
    try {
      await exportAgreementXlsx(agreement.id, { number: agreement.number });
    } catch (err) {
      setError(err.message || "Не удалось скачать соглашение");
    } finally {
      setDownloadingAgreementId(null);
    }
  };

  const handleDeleteClick = (agreementId, e) => {
    e.stopPropagation();
    setPendingDeleteId(agreementId);
  };

  const handleSendForApprovalClick = (agreement, e) => {
    e.stopPropagation();
    setAgreementToApprove(agreement);
  };

  const handleApprovalSuccess = () => {
    refreshAgreements();
  };

  const cancelDelete = () => {
    if (!isDeleting) setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setIsDeleting(true);
    try {
      await deleteAgreement(pendingDeleteId);
      setPendingDeleteId(null);
      setExpandedRows((prev) => {
        const next = new Set(prev);
        next.delete(pendingDeleteId);
        return next;
      });
      refreshAgreements();
    } catch (err) {
      setError(err.message || "Не удалось удалить соглашение");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteApprovalClick = (agreement, e) => {
    e.stopPropagation();
    setPendingDeleteApprovalAgreement(agreement);
  };

  const cancelDeleteApproval = () => {
    if (!isDeletingApproval) setPendingDeleteApprovalAgreement(null);
  };

  const confirmDeleteApproval = async () => {
    if (!pendingDeleteApprovalAgreement) return;
    if (
      !isAgreementApprovalInitiator(
        null,
        pendingDeleteApprovalAgreement,
        userData?.id,
      )
    ) {
      setError("Отменить согласование может только инициатор");
      setPendingDeleteApprovalAgreement(null);
      return;
    }
    setIsDeletingApproval(true);
    try {
      const approvalId = await resolveAgreementApprovalId(
        pendingDeleteApprovalAgreement,
      );
      if (approvalId == null) {
        throw new Error("Процесс согласования не найден");
      }
      await deleteAgreementApproval(approvalId);
      setPendingDeleteApprovalAgreement(null);
      refreshAgreements();
    } catch (err) {
      setError(err.message || "Не удалось отменить согласование");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  return (
    <div className="agreements-container">
      <div className="agreements-header">
        <h2 className="agreements-title">Субподрядчики проекта</h2>
      </div>

      <div className="agreements-controls">
        <div className="agreements-search">
          <input
            type="text"
            placeholder="Поиск по номеру, проекту, субподрядчику..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="agreements-search-input"
          />
        </div>
        <div className="agreements-filters">
          <select className="agreements-filter-select">
            <option>Все типы</option>
          </select>
          <select className="agreements-filter-select">
            <option>Все статусы</option>
          </select>
          <select className="agreements-filter-select">
            <option>Все разделы</option>
          </select>
        </div>
        <button
          className="agreements-add-btn"
          onClick={() => setAgreementModal({ open: true, agreementId: null })}
        >
          <span>+</span> Добавить соглашение
        </button>
      </div>

      {error && (
        <div className="agreements-error" role="alert">
          {error}
        </div>
      )}

      {loading && (
        <div className="agreements-loading">Загрузка соглашений...</div>
      )}

      <div className="agreements-table-container">
        <table className="agreements-table">
          <thead>
            <tr>
              <th>Номер</th>
              <th>Субподрядчик</th>
              <th>Разделы</th>
              <th>Сумма договора</th>
              <th>Этап</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              displayAgreements.map((agreement) => {
                const listStatus = resolveAgreementListStatus(agreement);
                return (
                  <React.Fragment key={agreement.id}>
                    <tr
                      className={`agreements-row ${
                        expandedRows.has(agreement.id) ? "expanded" : ""
                      }`}
                      onClick={() => toggleRowExpansion(agreement.id)}
                    >
                      <td className="agreement-number">
                        {formatAgreementNumber(agreement.number)}
                      </td>
                      <td>
                        <div className="agreement-subcontractor">
                          <span className="expand-arrow">
                            {expandedRows.has(agreement.id) ? "▼" : "▶"}
                          </span>
                          <span className="subcontractor-name">
                            {getAgreementPerformerName(agreement)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="agreement-sections">
                          {getAgreementSectionsLabels(agreement).length > 0 ? (
                            getAgreementSectionsLabels(agreement).map(
                              (label, idx) => (
                                <span key={idx} className="section-tag">
                                  {label}
                                </span>
                              ),
                            )
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      </td>
                      <td className="agreement-amount">
                        {formatCurrency(agreement.price ?? 0)} ₸
                      </td>
                      <td className="agreement-stage">
                        {agreement.stage_name != null
                          ? agreement.stage_name
                          : "—"}
                      </td>
                      <td>
                        <span
                          className={getAgreementListStatusBadgeClass(
                            listStatus,
                          )}
                        >
                          {AGREEMENT_LIST_STATUS_LABELS[listStatus] ??
                            listStatus ??
                            "—"}
                        </span>
                      </td>
                      <td>
                        <div
                          className="agreement-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="action-btn view-btn"
                            title="Скачать документ"
                            onClick={(e) =>
                              handleDownloadAgreementDocument(agreement, e)
                            }
                            disabled={
                              downloadingAgreementId === agreement.id
                            }
                          >
                            📄
                          </button>
                          <button
                            type="button"
                            className="action-btn edit-btn"
                            title={getEditAgreementTitle(agreement)}
                            disabled={!canEditAgreement(agreement)}
                            aria-label={getEditAgreementTitle(agreement)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canEditAgreement(agreement)) return;
                              setAgreementModal({
                                open: true,
                                agreementId: agreement.id,
                              });
                            }}
                          >
                            <EditIcon />
                          </button>
                          {canSendAgreementForApproval(agreement) && (
                            <button
                              type="button"
                              className="action-btn approve-btn"
                              title={getSendForApprovalTitle(agreement)}
                              onClick={(e) =>
                                handleSendForApprovalClick(agreement, e)
                              }
                            >
                              📤
                            </button>
                          )}
                          {canShowDeleteApprovalAction(
                            agreement,
                            userData?.id,
                          ) && (
                            <button
                              type="button"
                              className="action-btn cancel-approval-btn"
                              title={getDeleteApprovalTitle(agreement)}
                              onClick={(e) =>
                                handleDeleteApprovalClick(agreement, e)
                              }
                            >
                              ↩
                            </button>
                          )}
                          <button
                            type="button"
                            className="action-btn delete-btn"
                            title="Удалить"
                            onClick={(e) => handleDeleteClick(agreement.id, e)}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(agreement.id) && (
                      <tr className="agreement-details-row">
                        <td colSpan="7">
                          <AgreementDetail
                            agreementId={agreement.id}
                            inline
                            fallbackCounterpartyName={getAgreementPerformerName(
                              agreement,
                            )}
                            sectionNameById={sectionNameById}
                            onApprovalDeleted={refreshAgreements}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="agreements-pagination">
        <button
          type="button"
          className="pagination-btn"
          onClick={() => setApiPage((p) => Math.max(1, p - 1))}
          disabled={apiPage <= 1 || loading}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            type="button"
            className={`pagination-btn ${page === apiPage ? "active" : ""}`}
            onClick={() => setApiPage(page)}
            disabled={loading}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="pagination-btn"
          onClick={() => setApiPage((p) => Math.min(totalPages, p + 1))}
          disabled={apiPage >= totalPages || loading}
        >
          ›
        </button>
      </div>

      <AddAgreement
        open={agreementModal.open}
        agreementId={agreementModal.agreementId}
        onClose={() => {
          refreshAgreements();
          setAgreementModal({ open: false, agreementId: null });
        }}
        projectId={projectId}
      />

      <SendAgreementApprovalModal
        open={agreementToApprove != null}
        agreement={agreementToApprove}
        onClose={() => setAgreementToApprove(null)}
        onSuccess={handleApprovalSuccess}
      />

      {pendingDeleteApprovalAgreement != null && (
        <div
          className="agreements-popup-overlay"
          onClick={cancelDeleteApproval}
        >
          <div
            className="agreements-popup agreements-popup--confirm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreements-delete-approval-confirm-title"
          >
            <p
              id="agreements-delete-approval-confirm-title"
              className="agreements-popup-text"
            >
              {getDeleteApprovalTitle(pendingDeleteApprovalAgreement)}
            </p>
            <div className="agreements-popup-buttons">
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--cancel"
                onClick={cancelDeleteApproval}
                disabled={isDeletingApproval}
              >
                Нет
              </button>
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--confirm"
                onClick={confirmDeleteApproval}
                disabled={isDeletingApproval}
              >
                {isDeletingApproval ? "Удаление..." : "Отменить согласование"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteId != null && (
        <div className="agreements-popup-overlay" onClick={cancelDelete}>
          <div
            className="agreements-popup agreements-popup--confirm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreements-delete-confirm-title"
          >
            <p
              id="agreements-delete-confirm-title"
              className="agreements-popup-text"
            >
              Вы уверены что хотите удалить соглашение?
            </p>
            <div className="agreements-popup-buttons">
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agreements;
